import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FiGrid, FiUsers, FiBriefcase, FiDollarSign, FiClock, FiUser, FiUserCheck, FiBell } from "react-icons/fi";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useColorModeValue,
  Icon,
  Badge,
} from '@chakra-ui/react';
import { 
  HamburgerIcon, 
  PhoneIcon, 
  ViewIcon, 
  ChevronRightIcon,
  SettingsIcon,
  SearchIcon,
  CalendarIcon,
  StarIcon,
  BellIcon,
  ChatIcon,
  AtSignIcon
} from '@chakra-ui/icons';

import { useAuth } from '../../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { associateAPI } from '../../services/api';


const Layout: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Sidebar styled like the provided screenshot: white sidebar, purple active item
  const sidebarBg = 'white';
  const textColor = 'gray.800';
  const activeBg = 'primary.700';
  const activeColor = 'white';
  const inactiveColor = 'gray.700';
  const hoverInactiveBg = 'gray.100';


  // Navigation items based on role
  type NavItem = { path: string; label: string; icon: any };
  let navigationItems: NavItem[] = [];
  if (user?.role === 'user') {
    navigationItems = [
      { path: '/timesheet', label: 'Timesheet', icon: FiClock },
      { path: '/profile', label: 'Profile', icon: FiUser },
    ];
  } else if (user?.role === 'manager') {
    navigationItems = [
      { path: '/manager-timesheet-approval', label: 'Approve Timesheets', icon: FiUser },
      { path: '/profile', label: 'Profile', icon: FiUser },
    ];
  } else if (user?.role === 'hr') {
    navigationItems = [
      { path: '/', label: 'Dashboard', icon: FiGrid },
      { path: '/finance', label: 'Finance', icon: FiDollarSign },
      { path: '/profile', label: 'Profile', icon: FiUser },
    ];
  } else if (user?.role === 'admin') {
    navigationItems = [
      { path: '/', label: 'Dashboard', icon: FiGrid },
      { path: '/users', label: 'Users', icon: FiUser },
      { path: '/designations', label: 'Designations', icon: StarIcon }, 
      { path: '/clients', label: 'Clients', icon: FiUsers },
      { path: '/projects', label: 'Projects', icon: FiBriefcase },
      { path: '/associates', label: 'Associates', icon: FiUserCheck },
      { path: '/finance', label: 'Finance', icon: FiDollarSign },
      { path: '/profile', label: 'Profile', icon: FiUser },
    ];
  } else {
    navigationItems = [
      { path: '/', label: 'Dashboard', icon: FiGrid },
      { path: '/users', label: 'Users', icon: FiUser },
      { path: '/designations', label: 'Designations', icon: StarIcon }, 
      { path: '/clients', label: 'Clients', icon: FiUsers },
      { path: '/projects', label: 'Projects', icon: FiBriefcase },
      { path: '/associates', label: 'Associates', icon: FiUserCheck },
      { path: '/finance', label: 'Finance', icon: FiDollarSign },
      { path: '/timesheet', label: 'Timesheet', icon: FiClock },
      { path: '/profile', label: 'Profile', icon: FiUser },
    ];
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };


  // State to hold user/associate photo for sidebar avatar
  const [sidebarPhoto, setSidebarPhoto] = useState<string | undefined>(undefined);
  useEffect(() => {
    const fetchPhoto = async () => {
      if (user?.associateId) {
        try {
          const res = await associateAPI.getAssociateById(user.associateId);
          const photo = (res.data as any).photo;
          setSidebarPhoto(photo || undefined);
        } catch {
          setSidebarPhoto(undefined);
        }
      } else if (user && (user as any).avatar) {
        setSidebarPhoto((user as any).avatar || undefined);
      } else {
        setSidebarPhoto(undefined);
      }
    };
    fetchPhoto();
    // Listen for photo update events
    const handler = () => fetchPhoto();
    window.addEventListener('profile-photo-updated', handler);
    return () => {
      window.removeEventListener('profile-photo-updated', handler);
    };
  }, [user]);

  const SidebarContent = () => (
    <Flex direction="column" justify="space-between" h="100vh">
      <Box>
        {/* Logo/Brand */}
        <Box p={6} borderBottom="1px" borderColor="gray.200">
          <Text fontSize="xl" fontWeight="bold" color="primary.700">
            CMS
          </Text>
          <Text fontSize="sm" color="gray.500">
            Client Management System
          </Text>
        </Box>

        {/* Navigation */}
        <VStack spacing={2} p={4} align="stretch">
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Box
                key={item.path}
                w="full"
                as="button"
                onClick={() => {
                  navigate(item.path);
                  onClose();
                }}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
                bg={isActive ? activeBg : 'transparent'}
                color={isActive ? activeColor : inactiveColor}
                _hover={{
                  bg: isActive ? activeBg : hoverInactiveBg,
                  color: isActive ? activeColor : inactiveColor,
                }}
                transition="all 0.2s"
                borderRadius="lg"
                p={3}
              >
                <HStack spacing={3} justify="space-between" w="full">
                  <HStack spacing={3}>
                    <Icon as={IconComponent as any} boxSize={5} />
                    <Text fontWeight="medium">{item.label}</Text>
                  </HStack>
                  {item.path === '/notifications' && (
                    <Badge colorScheme="blue" size="sm" borderRadius="full">
                      6
                    </Badge>
                  )}
                </HStack>
              </Box>
            );
          })}
        </VStack>
      </Box>

      {/* Profile navigation and User Profile at bottom with logout icon */}
      <Box p={4} borderTop="1px" borderColor="gray.200">
        <HStack spacing={3}>
          <Avatar size="sm" name={user?.name} src={sidebarPhoto} />
          <VStack align="start" spacing={0} flex={1}>
            <Text fontSize="sm" fontWeight="medium" color={inactiveColor}>
              {user?.name || 'User'}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {user?.role || 'Employee'}
            </Text>
          </VStack>
          <IconButton
            aria-label="Logout"
            icon={<ChevronRightIcon />}
            variant="ghost"
            borderColor="red.500"
            borderWidth={"1px"}
            colorScheme="red"
            onClick={handleLogout}
            size="sm"
            title="Logout"
          />
        </HStack>
      </Box>
    </Flex>
  );

  return (
    <Box minH="100vh" bg="background.page">
      {/* Mobile Header */}
      <Flex
        display={{ base: 'flex', lg: 'none' }}
        bg="white"
        px={4}
        py={3}
        borderBottom="1px"
        borderColor="gray.200"
        align="center"
        justify="space-between"
      >
        <IconButton
          aria-label="Open menu"
          icon={<HamburgerIcon />}
          variant="ghost"
          onClick={onOpen}
        />
        <Text fontSize="lg" fontWeight="bold" color="primary.700">
          CMS
        </Text>
        <Box w={10} />
      </Flex>

      {/* Desktop Sidebar - Fixed */}
      <Box
        display={{ base: 'none', lg: 'block' }}
        w="280px"
        minW="280px"
        maxW="280px"
        bg={sidebarBg}
        minH="100vh"
        position="fixed"
        top={0}
        left={0}
        zIndex={100}
        borderRight="1px"
        borderColor="gray.200"
      >
        <SidebarContent />
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="280px">
        <DrawerOverlay />
        <DrawerContent bg={sidebarBg} maxW="280px" w="280px">
          <DrawerCloseButton color={textColor} />
          <DrawerHeader borderBottom="1px" borderColor="gray.200">
            <Text color={textColor}>CMS</Text>
          </DrawerHeader>
          <DrawerBody p={0}>
            <SidebarContent />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Main Content - add left margin for sidebar */}
      <Box flex={1} minW={0} p={6} ml={{ base: 0, lg: '280px' }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;